/**
 * Theme state for the application.
 *
 * Holds the user's *preference* (which may be "system") and the *resolved*
 * theme separately, because a settings screen needs to show which of the three
 * options is selected — not merely whether the page is currently dark.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { ThemeContext } from "./context";
import {
  applyTheme,
  readStoredTheme,
  resolveTheme,
  storeTheme,
  type ResolvedTheme,
  type ThemePreference,
} from "./theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));

  useEffect(() => {
    setResolved(applyTheme(preference));
  }, [preference]);

  useEffect(() => {
    // Follow the OS live while the preference is "system". A user switching
    // their machine to dark at sunset should see the app follow, not wait for
    // a reload.
    if (preference !== "system") return;
    // Guarded, like `systemTheme()` in `theme.ts`. The inconsistency was the
    // bug: one place tolerated an environment with no `matchMedia` and the
    // other crashed on it, so a component tree rendered under jsdom — or in
    // any embedding without the API — died on the provider rather than
    // falling back to the light palette.
    if (!window.matchMedia) return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      setResolved(applyTheme("system"));
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    storeTheme(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
